import React from "react";
import { RefreshCw, Save, Radio, Bell, BellOff, Upload } from "lucide-react";
import { TtsQuotaLight } from "./TtsQuotaLight";

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const VOICE_ID_DEFAULT = "u8M9fG4euhSqnMmNr3gI";

export function PodcastSettingsPanel({
  configForm, onConfigChange,
  config,
  connectForm, onConnectChange,
  connecting, savingConfig, uploadingStyleSheet,
  pushPermission, pushSubscribed, pushLoading, pushSubCount,
  headers,
  dm, c, btnPrimary, btnSecondary, inputCls, labelCls,
  onSave, onSaveCredentials, onSpreakerOAuth, onUploadStyleSheet,
  onSubscribePush, onUnsubscribePush, onSendTestPush,
}) {
  return (
    <div className="space-y-4 max-w-2xl">
      {/* Schedule */}
      <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-white border border-[#f0f1f2]"}`}>
        <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${c.muted}`}>
          Autopilot Schedule
        </p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: "schedule_day",   label: "Selection Day",  type: "select" },
            { key: "script_day",     label: "Script Day",     type: "select" },
            { key: "publish_day",    label: "Publish Day",    type: "select" },
            { key: "schedule_hour",  label: "Selection Hour", type: "number" },
            { key: "script_hour",    label: "Script Hour",    type: "number" },
            { key: "publish_hour",   label: "Publish Hour",   type: "number" },
          ].map(f => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              {f.type === "select" ? (
                <select
                  value={configForm[f.key] || ""}
                  onChange={e => onConfigChange(f.key, e.target.value)}
                  className={inputCls}
                >
                  {DAYS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={configForm[f.key] ?? ""}
                  onChange={e => onConfigChange(f.key, Number(e.target.value))}
                  className={inputCls}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4">
          <div>
            <p className={`text-xs font-semibold ${c.text}`}>Autopilot</p>
            <p className={`text-[10px] ${c.muted}`}>
              Run all 3 steps automatically without intervention
            </p>
          </div>
          <button
            onClick={() => onConfigChange("autopilot_enabled", !configForm.autopilot_enabled)}
            className={`w-10 h-5 rounded-full transition-colors relative ${
              configForm.autopilot_enabled
                ? "bg-cyan-500"
                : (dm ? "bg-slate-700" : "bg-slate-200")
            }`}
            data-testid="autopilot-toggle"
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${
              configForm.autopilot_enabled ? "left-5" : "left-0.5"
            }`} />
          </button>
        </div>
      </div>

      {/* ElevenLabs */}
      <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-white border border-[#f0f1f2]"}`}>
        <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${c.muted}`}>
          ElevenLabs Voice
        </p>

        {/* Model selector */}
        <div className="mb-3">
          <label className={labelCls}>TTS Model</label>
          <select
            value={configForm.elevenlabs_model || "eleven_v3"}
            onChange={e => onConfigChange("elevenlabs_model", e.target.value)}
            className={inputCls}
            data-testid="elevenlabs-model-select"
          >
            <option value="eleven_v4">eleven_v4 — Text to Dialogue · audio tags, IPA, stitching (fallback v2)</option>
            <option value="eleven_v3">eleven_v3 — Best quality · 5-min chunks (alpha)</option>
            <option value="eleven_multilingual_v2">eleven_multilingual_v2 — High quality · multilingual</option>
            <option value="eleven_turbo_v2_5">eleven_turbo_v2_5 — Fast · cost-efficient</option>
            <option value="eleven_flash_v2_5">eleven_flash_v2_5 — Fastest · most economical</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>API Key</label>
            <input
              type="password"
              value={configForm.elevenlabs_api_key || ""}
              onChange={e => onConfigChange("elevenlabs_api_key", e.target.value)}
              className={inputCls}
              data-testid="elevenlabs-api-key-input"
            />
          </div>
          <div>
            <label className={labelCls}>Voice ID</label>
            <input
              type="text"
              value={configForm.elevenlabs_voice_id || VOICE_ID_DEFAULT}
              onChange={e => onConfigChange("elevenlabs_voice_id", e.target.value)}
              className={inputCls}
              data-testid="elevenlabs-voice-id-input"
            />
          </div>
        </div>
        <TtsQuotaLight headers={headers} dm={dm} c={c} />

        {(configForm.elevenlabs_model || "").startsWith("eleven_v4") && (
          <div className={`mt-3 rounded-lg p-3 space-y-2 ${dm ? "bg-slate-800/60" : "bg-slate-50"}`} data-testid="eleven-v4-options">
            <p className={`text-[10px] font-bold uppercase tracking-widest ${c.muted}`}>Eleven v4 · Text to Dialogue</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Stability (v4)</label>
                <input type="number" step="0.05" min="0" max="1" value={configForm.v4_stability ?? 0.5}
                  onChange={e => onConfigChange("v4_stability", parseFloat(e.target.value))} className={inputCls} data-testid="v4-stability-input" />
              </div>
              <div>
                <label className={labelCls}>Block chars</label>
                <input type="number" step="100" min="500" max="9500" value={configForm.v4_block_chars ?? 8500}
                  onChange={e => onConfigChange("v4_block_chars", parseInt(e.target.value, 10))} className={inputCls} data-testid="v4-block-chars-input" />
              </div>
              <div>
                <label className={labelCls}>Language</label>
                <input type="text" value={configForm.v4_language_code || "en"}
                  onChange={e => onConfigChange("v4_language_code", e.target.value)} className={inputCls} data-testid="v4-language-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Pronunciation dictionary ID</label>
                <input type="text" value={configForm.pronunciation_dictionary_id || ""} placeholder="XQbzm645C4EExad3MKxs (default)"
                  onChange={e => onConfigChange("pronunciation_dictionary_id", e.target.value)} className={inputCls} data-testid="v4-dict-id-input" />
              </div>
              <div>
                <label className={labelCls}>Dictionary version ID</label>
                <input type="text" value={configForm.pronunciation_dictionary_version_id || ""} placeholder="C4Rry9xGlONvcquqcyQQ (default)"
                  onChange={e => onConfigChange("pronunciation_dictionary_version_id", e.target.value)} className={inputCls} data-testid="v4-dict-version-input" />
              </div>
            </div>
            <p className={`text-[11px] ${c.muted}`}>Blocks are cut at paragraph boundaries (docs: ≤ 2,000 chars/request for reliable generation). Audio tags like [warm] and inline IPA /…/ in the script are passed through; the seed is fixed per episode. If v4 fails twice on a block the episode is rendered with multilingual_v2 (tags/IPA stripped) and MC is notified.</p>
          </div>
        )}

        {/* Voice quality parameters */}
        <div className="mt-4 space-y-3">
          <p className={`text-[10px] font-bold uppercase tracking-widest ${c.muted}`}>Voice Parameters</p>
          {[
            { key: "el_stability",        label: "Stability",        hint: "Low = expressive · High = consistent",     default: 0.50 },
            { key: "el_similarity_boost", label: "Similarity Boost", hint: "How closely to match the original voice",  default: 0.80 },
            { key: "el_style",            label: "Style Exaggeration",hint: "Amplifies speaking style (0 = neutral)",  default: 0.10 },
          ].map(p => {
            const val = configForm[p.key] !== undefined ? configForm[p.key] : p.default;
            return (
              <div key={p.key}>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelCls}>{p.label}</label>
                  <span className={`text-[11px] font-mono font-semibold ${c.text}`}>{Number(val).toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min={0} max={1} step={0.01}
                  value={val}
                  onChange={e => onConfigChange(p.key, parseFloat(e.target.value))}
                  className="w-full accent-cyan-500 cursor-pointer"
                  data-testid={`${p.key}-slider`}
                />
                <p className={`text-[10px] mt-0.5 ${c.muted}`}>{p.hint}</p>
              </div>
            );
          })}

          {/* Speaker Boost toggle */}
          <div className="flex items-center justify-between pt-1">
            <div>
              <p className={`text-xs font-semibold ${c.text}`}>Speaker Boost</p>
              <p className={`text-[10px] ${c.muted}`}>Enhances clarity of speech — recommended for podcasts</p>
            </div>
            <button
              onClick={() => onConfigChange("el_use_speaker_boost", !(configForm.el_use_speaker_boost !== false))}
              className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${
                configForm.el_use_speaker_boost !== false ? "bg-cyan-500" : (dm ? "bg-slate-700" : "bg-slate-200")
              }`}
              data-testid="speaker-boost-toggle"
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${
                configForm.el_use_speaker_boost !== false ? "left-5" : "left-0.5"
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Spreaker OAuth2 */}
      <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-white border border-[#f0f1f2]"}`}>
        <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${c.muted}`}>
          Spreaker
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={labelCls}>Client ID</label>
            <input
              type="text"
              value={connectForm.client_id}
              onChange={e => onConnectChange("client_id", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Client Secret</label>
            <input
              type="password"
              value={connectForm.client_secret}
              onChange={e => onConnectChange("client_secret", e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
        <div className="flex-1">
          <label className={labelCls}>Show ID</label>
          <input
            type="text"
            value={configForm.spreaker_show_id || ""}
            onChange={e => onConfigChange("spreaker_show_id", e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={onSaveCredentials} className={btnSecondary}>
            <Save className="w-3 h-3" /> Save Credentials
          </button>
          <button onClick={onSpreakerOAuth} disabled={connecting} className={btnSecondary}>
            {connecting
              ? <RefreshCw className="w-3 h-3 animate-spin" />
              : <Radio className="w-3 h-3" />}
            {config.spreaker_connected ? "Re-connect" : "Connect Spreaker"}
          </button>
          {config.spreaker_connected && (
            <span className="text-xs text-emerald-400 self-center">Connected</span>
          )}
        </div>
      </div>

      {/* AI Personalisation */}
      <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-white border border-[#f0f1f2]"}`}>
        <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${c.muted}`}>
          AI Personalisation
        </p>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>
              Global Custom Prompt (applied to every episode)
            </label>
            <textarea
              rows={4}
              value={configForm.custom_prompt || ""}
              onChange={e => onConfigChange("custom_prompt", e.target.value)}
              className={`${inputCls} resize-none text-xs`}
              placeholder="e.g. 'Always open with a rhetorical question. Keep tone direct and provocative.'"
            />
          </div>
          <div>
            <label className={labelCls}>Style Sheet (.txt or .md)</label>
            <label className={`${btnSecondary} cursor-pointer inline-flex`}>
              {uploadingStyleSheet
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Upload className="w-3.5 h-3.5" />}
              {configForm.style_sheet_filename
                ? `"${configForm.style_sheet_filename}" — change`
                : "Upload style sheet"}
              <input
                type="file"
                accept=".txt,.md"
                className="hidden"
                onChange={e => onUploadStyleSheet(e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
      </div>

      <button
        onClick={onSave}
        disabled={savingConfig}
        className={`w-full justify-center ${btnPrimary}`}
        data-testid="save-podcast-config-btn"
      >
        {savingConfig
          ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          : <Save className="w-3.5 h-3.5" />}
        Save Settings
      </button>

      {/* Push Notifications */}
      <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-white border border-[#f0f1f2]"}`}>
        <div className="flex items-center justify-between mb-1">
          <p className={`text-xs font-bold uppercase tracking-widest ${c.muted}`}>
            Push Notifications
          </p>
          {pushSubCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 font-bold">
              {pushSubCount} device{pushSubCount !== 1 ? "s" : ""} subscribed
            </span>
          )}
        </div>
        <p className={`text-[11px] mb-3 ${c.muted}`}>
          Get notified at each pipeline step — selection, script, audio, publish.
        </p>
        {!("Notification" in window) || !("PushManager" in window) ? (
          <p className={`text-xs italic ${c.muted}`}>Not supported in this browser.</p>
        ) : pushPermission === "denied" ? (
          <p className="text-xs text-red-400">Permission denied — allow in browser settings.</p>
        ) : !pushSubscribed ? (
          <button
            onClick={onSubscribePush}
            disabled={pushLoading}
            className={btnPrimary}
            data-testid="push-subscribe-btn"
          >
            {pushLoading
              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              : <Bell className="w-3.5 h-3.5" />}
            Enable Notifications
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={onSendTestPush}
              disabled={pushLoading}
              className={btnSecondary}
              data-testid="push-test-btn"
            >
              {pushLoading
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Bell className="w-3.5 h-3.5" />}
              Send Test
            </button>
            <button
              onClick={onUnsubscribePush}
              disabled={pushLoading}
              className={`${btnSecondary} text-red-400`}
              data-testid="push-unsubscribe-btn"
            >
              <BellOff className="w-3.5 h-3.5" /> Disable
            </button>
          </div>
        )}
        {pushSubscribed && (
          <p className="text-[10px] mt-2 text-emerald-400">Active on this device</p>
        )}
      </div>
    </div>
  );
}
